/*
  Vayu Sleep – homepage redesign behaviour.
  - Email signup forms: subscribe to the Klaviyo list set in Theme settings > Vayu email signup.
    Without a list ID the form falls back to Shopify's own customer signup (normal form post).
  - "Why fit" counter animation.
  - Pauses decorative background videos for visitors who prefer reduced motion.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var KLAVIYO_REVISION = '2024-10-15';

  /* ---------- Klaviyo signup ---------- */

  function klaviyoCompanyId() {
    var config = window.vayuConfig || {};
    if (config.klaviyoPublicKey) return config.klaviyoPublicKey;

    // Fall back to the public key used by the Klaviyo app embed, if it is on the page.
    var scripts = document.querySelectorAll('script[src*="klaviyo.com"]');
    for (var i = 0; i < scripts.length; i++) {
      var match = scripts[i].src.match(/company_id=([A-Za-z0-9]+)/) || scripts[i].src.match(/onsite\/js\/([A-Za-z0-9]+)\//);
      if (match) return match[1];
    }
    return '';
  }

  function setMessage(wrapper, text, isError) {
    var message = wrapper.querySelector('.vayu-form__message');
    if (!message) return;
    message.textContent = text || '';
    message.classList.toggle('vayu-form__message--error', !!isError);
  }

  function subscribe(email, source) {
    var config = window.vayuConfig || {};
    var companyId = klaviyoCompanyId();

    return fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: { 'Signup source': source },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: config.klaviyoListId } }
          }
        }
      })
    }).then(function (response) {
      if (!response.ok) throw new Error('Klaviyo responded with ' + response.status);
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var wrapper = form.closest && form.closest('[data-vayu-signup]');
    if (!wrapper) return;

    var config = window.vayuConfig || {};
    // Not connected to Klaviyo yet: let Shopify's customer form submit normally.
    if (!config.klaviyoListId || !klaviyoCompanyId()) return;

    event.preventDefault();

    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('button[type="submit"]');
    var email = (input.value || '').trim();

    if (!input.checkValidity() || !email) {
      setMessage(wrapper, wrapper.dataset.invalid, true);
      input.focus();
      return;
    }

    setMessage(wrapper, '');
    button.disabled = true;

    subscribe(email, wrapper.dataset.source || 'Website')
      .then(function () {
        wrapper.classList.add('is-success');
        var success = wrapper.querySelector('.vayu-form__success');
        if (success) {
          success.setAttribute('tabindex', '-1');
          success.focus({ preventScroll: true });
        }
      })
      .catch(function () {
        setMessage(wrapper, wrapper.dataset.error, true);
      })
      .then(function () {
        button.disabled = false;
      });
  });

  /* ---------- Mobile menu ---------- */

  function setMenu(button, open) {
    var panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
  }

  function closeMenus(focusButton) {
    document.querySelectorAll('[data-vayu-menu-toggle][aria-expanded="true"]').forEach(function (button) {
      setMenu(button, false);
      if (focusButton) button.focus();
    });
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var button = event.target.closest('[data-vayu-menu-toggle]');
    if (button) {
      setMenu(button, button.getAttribute('aria-expanded') !== 'true');
      return;
    }

    // Close after following a link inside the menu, or when clicking outside it.
    if (event.target.closest('.vayu-header__panel a')) {
      closeMenus(false);
    } else if (!event.target.closest('.vayu-header')) {
      closeMenus(false);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') closeMenus(true);
  });

  window.addEventListener('resize', function () {
    if (window.innerWidth >= 990) closeMenus(false);
  });

  /* ---------- Counter ---------- */

  var DURATION = 2000;
  var HOLD = 1400;

  function initCounter(el) {
    if (el.dataset.vayuReady) return;
    el.dataset.vayuReady = 'true';

    var start = parseFloat(el.dataset.start) || 0;
    var end = parseFloat(el.dataset.end) || 0;
    var loop = el.dataset.mode === 'loop';
    var valueEl = el.querySelector('[data-counter-value]');
    var fill = el.querySelector('.vayu-stat__fill');
    var raf = null;
    var startedAt = null;
    var finished = false;

    function render(t) {
      valueEl.textContent = Math.round(start + (end - start) * t);
      if (fill) fill.style.setProperty('--progress', (t * 100).toFixed(1) + '%');
    }

    function tick(now) {
      if (startedAt === null) startedAt = now;
      var elapsed = now - startedAt;
      if (loop) elapsed = elapsed % (DURATION + HOLD);
      var t = elapsed >= DURATION ? 1 : elapsed / DURATION;
      render(1 - Math.pow(1 - t, 3));

      if (!loop && t === 1) {
        finished = true;
        raf = null;
        el.classList.add('is-measured');
        return;
      }
      raf = requestAnimationFrame(tick);
    }

    function play() {
      if (raf || finished) return;
      startedAt = null;
      raf = requestAnimationFrame(tick);
    }

    function pause() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    }

    if (el.dataset.mode === 'off' || reduceMotion.matches || !('IntersectionObserver' in window)) {
      render(1);
      el.classList.add('is-measured');
      return;
    }

    render(0);
    if (loop) el.classList.add('is-measured');

    new IntersectionObserver(function (entries) {
      entries[0].isIntersecting ? play() : pause();
    }, { threshold: 0.45 }).observe(el);
  }

  /* ---------- Airflow: runs only while its section is on screen ---------- */

  function initFlow(section) {
    if (section.dataset.vayuFlowReady) return;
    section.dataset.vayuFlowReady = 'true';

    if (!section.querySelector('.vayu-lines')) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      section.classList.toggle('is-flowing', entries[0].isIntersecting);
    }, { threshold: 0 }).observe(section);
  }

  /* ---------- How it works: one-time staggered settle ---------- */

  /* Benefit icons: one pass, left to right, the first time the row is reached. */
  function initBenefits(el) {
    if (el.dataset.vayuIconsReady) return;
    el.dataset.vayuIconsReady = 'true';

    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) return;

    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      el.classList.add('is-revealed');
      observer.disconnect();
    }, { threshold: 0.25 });
    observer.observe(el);
  }

  function initSteps(el) {
    if (el.dataset.vayuStepsReady) return;
    el.dataset.vayuStepsReady = 'true';

    var steps = el.querySelectorAll('.vayu-step');
    if (!steps.length) return;

    // Phones read the steps one at a time, so they stay static there.
    var wideEnough = window.matchMedia('(min-width: 750px)').matches;
    if (!wideEnough || reduceMotion.matches || !('IntersectionObserver' in window)) return;

    // Already on screen (e.g. an anchor link): leave it alone rather than animating late.
    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) return;

    steps.forEach(function (step, i) { step.style.setProperty('--i', i); });
    el.setAttribute('data-vayu-reveal', '');

    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      el.classList.add('is-revealed');
      observer.disconnect();
    }, { threshold: 0.2 });
    observer.observe(el);
  }

  /* ---------- Reduced motion for background videos ---------- */

  function initVideos(root) {
    if (!reduceMotion.matches) return;
    root.querySelectorAll('.vayu video[autoplay]').forEach(function (video) {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }

  function init(root) {
    root = root || document;
    root.querySelectorAll('[data-vayu-counter]').forEach(initCounter);
    root.querySelectorAll('.vayu-hero, .vayu-signup').forEach(initFlow);
    root.querySelectorAll('.vayu-steps').forEach(initSteps);
    root.querySelectorAll('.vayu-benefits').forEach(initBenefits);
    initVideos(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-initialise a section after it is edited.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
